import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import { HipaaLogo } from "~/icons";
import { visitTypePreferenceSchema } from "~/schemas/SignUp/Location";
import { RadioCardGroup } from "~/shared/components/ui/RadioCardGroup";
import { useAutoAdvanceOnSelect } from "~/shared/hooks/useAutoAdvanceOnSelect";
import { useSubStepTracking } from "~/shared/hooks/useFunnelTracking";
import { VISIT_TYPES } from "~/shared/utils/constants";
import { useSignUpStore } from "~/stores";
import { ActionButtons } from "../../ActionButtons";
import { ErrorMessage } from "../../ErrorMessage";
import { FormContentContainer } from "../../FormContentContainer";

interface VisitTypePreferenceFormProps {
  onPrev: () => void;
  onNext: (response?: string) => void;
}

export const VisitTypePreferenceForm = ({
  onPrev,
  onNext,
  stepId = "visit_type",
}: VisitTypePreferenceFormProps & { stepId?: string }) => {
  const { patient, setPatientField } = useSignUpStore();
  const { visitType } = patient;

  const defaultValue = visitType;
  const [selectedValue, setSelectedValue] = useState<string>(
    defaultValue ?? "",
  );
  const autoAdvanceOnSelect = useAutoAdvanceOnSelect(Boolean(defaultValue));

  const { trackComplete, trackBack, trackErrors } = useSubStepTracking({
    parentStep: "location",
    subStep: "visit_type",
  });

  const visitTypeOptions = VISIT_TYPES.map((type) => ({
    value: type,
    label: type,
  }));

  const {
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<z.infer<typeof visitTypePreferenceSchema>>({
    mode: "onBlur",
    resolver: zodResolver(visitTypePreferenceSchema),
    defaultValues: {
      visitType: defaultValue ?? "",
    },
  });

  useEffect(() => {
    setValue("visitType", defaultValue ?? "");
    setSelectedValue(defaultValue ?? "");
  }, [defaultValue, setValue]);

  const handleOptionChange = (value: string) => {
    setSelectedValue(value);
    setValue("visitType", value);
    // On the frontier (first encounter), a selection advances immediately;
    // on a revisited step the user changes freely and clicks Next.
    if (autoAdvanceOnSelect) {
      void handleSubmit(onSubmit, onError)();
    }
  };

  const onSubmit: SubmitHandler<z.infer<typeof visitTypePreferenceSchema>> = (
    data,
  ) => {
    setPatientField("visitType", data.visitType);
    trackComplete({ visit_type: data.visitType });
    onNext(data.visitType);
  };

  const onError = (errors: FieldErrors) => {
    trackErrors(errors);
  };

  const handlePrev = () => {
    setPatientField("visitType", undefined);
    trackBack();
    onPrev();
  };

  const canProceed = selectedValue !== "";

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      className="rounded-lg shadow-xl"
    >
      <FormContentContainer stepId={stepId}>
        <HipaaLogo className="size-16 self-center sm:size-20" />
        <h1 className="font-display text-xl sm:text-2xl">
          Do you prefer in-person or telehealth visits?
        </h1>

        <RadioCardGroup
          options={visitTypeOptions}
          value={selectedValue}
          onChange={handleOptionChange}
        />

        {errors.visitType && (
          <ErrorMessage message={errors.visitType.message ?? ""} />
        )}
      </FormContentContainer>
      <ActionButtons
        onPrev={handlePrev}
        isNextDisabled={!canProceed}
        helperText="Select an option to continue"
      />
    </form>
  );
};
